/**
 * InlineProductCard — V02 "Tools-page DNA"
 *
 * All visual styling inlined to bypass Next.js dev CSS-chunk caching.
 */

import '../../app/mockups/_styles/editorial.css'
import * as React from 'react'
import { ArrowUpRight } from 'lucide-react'
import { formatPrice } from '@/lib/format-utils'
import { productCardCtaName } from '@/lib/card-link-names'
import { dealCtaLabel } from '@/lib/deal-cta'
import { CardImage } from '@/components/brand-kit/card-image'
import { alternateCardImage } from '@/lib/card-image'

export interface InlineProductCardProps {
  brand: string
  name: string
  imageUrl: string
  imageAlt?: string
  /** FRIDGE-949: the product's curated image array; the first entry that is not
   *  the lead becomes the hover / focus alternate through the shared frame.
   *  Absent or single-image: the lead stays, no swap. */
  images?: readonly string[] | null
  price: string | number
  originalPrice?: string | number
  rating: number
  reviewCount: number
  badge?: string
  href: string
  onClick?: () => void
}

const BLUE = '#0469f0'
const ORANGE = '#ff9951'
const ORANGE_HOVER = '#ff8533'
const GRAY_900 = '#111827'
const GRAY_500 = '#6b7280'
const GRAY_400 = '#9ca3af'
const GRAY_300 = '#d1d5db'
const GRAY_200 = '#e5e7eb'

const Stars: React.FC<{ rating: number; count: number }> = ({ rating, count }) => {
  const full = Math.floor(rating)
  const half = rating - full >= 0.4
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 1, fontSize: 13, color: GRAY_300 }}>
      {[0, 1, 2, 3, 4].map((i) => {
        const filled = i < full || (i === full && half)
        return (
          <span key={i} aria-hidden style={{ color: filled ? ORANGE : GRAY_300, fontSize: 13 }}>
            ★
          </span>
        )
      })}
      <span style={{ marginLeft: 8, fontWeight: 800, color: '#000000', fontSize: 13 }}>
        {rating.toFixed(1)}
      </span>
      <span style={{ marginLeft: 4, color: '#000000', fontWeight: 500, fontSize: 12 }}>
        ({count.toLocaleString()})
      </span>
    </span>
  )
}

export const InlineProductCard: React.FC<InlineProductCardProps> = ({
  brand,
  name,
  imageUrl,
  images,
  imageAlt,
  price,
  originalPrice,
  rating,
  reviewCount,
  badge,
  href,
  onClick,
}) => {
  const numericPrice = Number(price) || 0
  const numericOriginal = originalPrice != null ? Number(originalPrice) || 0 : 0
  // FRIDGE-429 — `hasPrice` already gates the whole price column below; the
  // CTA label is derived from that SAME boolean, never from the raw `price`
  // prop (a formatted string like "$1,299" is NaN here, so the card paints no
  // price and the CTA must say so too).
  const hasPrice = Number.isFinite(numericPrice) && numericPrice > 0
  const savings = numericOriginal > numericPrice ? numericOriginal - numericPrice : 0
  const [hovered, setHovered] = React.useState(false)

  return (
    <article
      data-product-image-card
      className="ed-inline-product-grid"
      style={{
        position: 'relative',
        background: '#fff',
        border: `1px solid ${GRAY_200}`,
        borderLeft: `6px solid ${BLUE}`,
        borderRadius: 8,
        overflow: 'hidden',
        margin: '24px 0',
        transition: 'border-color 180ms ease, box-shadow 180ms ease, transform 180ms ease',
        boxShadow: hovered ? '0 16px 36px rgba(4, 105, 240, 0.10)' : 'none',
        transform: hovered ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      {/* Card-wide stretched link — makes the WHOLE card surface (price, brand,
          rating, gaps) navigate to the PDP as a single unit. aria-hidden +
          tabIndex=-1 keep it out of the a11y tree; the visible image, title and
          CTA anchors below remain the focusable, semantic links. The real
          anchors are raised (zIndex 2) so they stay independently clickable. */}
      <a
        href={href}
        onClick={onClick}
        aria-hidden="true"
        tabIndex={-1}
        style={{ position: 'absolute', inset: 0, zIndex: 1 }}
      >
        <span className="sr-only">{name}</span>
      </a>
      <a
        href={href}
        onClick={onClick}
        // WCAG 2.5.3, label in name (axe 4.14 label-content-name-mismatch,
        // 10/15/2026): the badge is visible text inside this link, so the
        // link's name carries it after the product name.
        aria-label={badge ? `${name}, ${badge}` : name}
        // FRIDGE-1576: the forced-colors focus ring for this link is drawn by
        // app/globals.css on this class, above the photo frame it wraps.
        className="ipc-image-link"
        style={{
          position: 'relative',
          zIndex: 2,
          background: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          height: 200,
          overflow: 'hidden',
          textDecoration: 'none',
          borderBottom: 'none',
          alignSelf: 'stretch',
        }}
      >
        <span className="sr-only">{name}</span>
        {/* FRIDGE-949: the same whole-appliance frame every card uses (CardImage —
            consistent scale, mouse-hover alternate when the array has one, no
            toggle button here because the card is one stretched link). */}
        <CardImage
          src={imageUrl}
          alternate={alternateCardImage(imageUrl, images ?? [])}
          alt={(imageAlt ?? name).endsWith(" | Fridge.com") ? (imageAlt ?? name) : `${imageAlt ?? name} | Fridge.com`}
          width={200}
          height={200}

          className="ipc-image"
        />
        {badge && (
          <span
            style={{
              position: 'absolute',
              top: 10,
              left: 10,
              background: GRAY_900,
              color: '#fff',
              fontSize: 9,
              fontWeight: 800,
              letterSpacing: '0.1em',
              padding: '4px 8px',
              borderRadius: 3,
              textTransform: 'uppercase',
              zIndex: 2,
            }}
          >
            {badge}
          </span>
        )}
      </a>

      <div style={{ padding: '14px 18px', display: 'flex', flexDirection: 'column' }}>
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 7,
            fontSize: 10,
            fontWeight: 800,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: BLUE,
            marginBottom: 10,
          }}
        >
          <span
            aria-hidden
            style={{ width: 5, height: 5, borderRadius: '50%', background: BLUE, display: 'inline-block' }}
          />
          {brand}
        </div>

        {/* FRIDGE-325 — a card title is a link label, not a document section.
            This card is injected INTO the article body at the blog-pc anchors,
            so its <h3> landed inside the author's own heading outline and
            competed with the article's real H3s. Rendered as a <p>; the title
            is still the accessible name of the link inside it, and every style
            is carried over verbatim (no visual change). */}
        <p
          style={{
            fontSize: 18,
            fontWeight: 800,
            letterSpacing: '-0.015em',
            lineHeight: 1.2,
            color: GRAY_900,
            margin: '0 0 6px',
            display: '-webkit-box',
            WebkitLineClamp: 3,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
          }}
        >
          <a
            href={href}
            onClick={onClick}
            style={{
              color: 'inherit',
              textDecoration: 'none',
              borderBottom: 'none',
            }}
          >
            {name}
          </a>
        </p>

        <div style={{ height: 1, background: GRAY_200, margin: '8px 0' }} />

        <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '4px 0' }}>
          {Number.isFinite(rating) && rating > 0 && rating <= 5 && Number.isFinite(reviewCount) && reviewCount > 0 && (
            <Stars rating={rating} count={reviewCount} />
          )}
        </div>

        <div style={{ height: 1, background: GRAY_200, margin: '8px 0' }} />

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-end',
            gap: 12,
            flexWrap: 'wrap',
            marginTop: 'auto',
            paddingTop: 8,
          }}
        >
          {hasPrice ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', lineHeight: 1 }}>
              {numericOriginal > numericPrice && (
                <span
                  style={{
                    fontSize: 12,
                    color: GRAY_400,
                    textDecoration: 'line-through',
                    marginBottom: 4,
                    fontWeight: 600,
                  }}
                >
                  {formatPrice(numericOriginal)}
                </span>
              )}
              <span
                style={{
                  fontSize: 24,
                  fontWeight: 900,
                  letterSpacing: '-0.02em',
                  color: GRAY_900,
                }}
              >
                {formatPrice(numericPrice)}
              </span>
              {savings > 0 && (
                <span
                  style={{
                    display: 'inline-block',
                    marginTop: 6,
                    fontSize: 10,
                    fontWeight: 800,
                    letterSpacing: '0.12em',
                    textTransform: 'uppercase',
                    color: ORANGE,
                    background: 'rgba(255, 153, 81, 0.12)',
                    padding: '3px 7px',
                    borderRadius: 3,
                  }}
                >
                  Save {formatPrice(savings)}
                </span>
              )}
            </div>
          ) : (
            <span aria-hidden />
          )}
          <a
            href={href}
            onClick={onClick}
            aria-label={productCardCtaName(dealCtaLabel(hasPrice), { title: name }, href)}
            style={{
              position: 'relative',
              zIndex: 2,
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
              background: ORANGE,
              color: 'var(--color-brand-action-text, #1a1a1a)',
              fontSize: 14,
              fontWeight: 700,
              letterSpacing: 0,
              textTransform: 'none',
              padding: '10px 16px',
              borderRadius: 8,
              textDecoration: 'none',
              borderBottom: 'none',
              cursor: 'pointer',
              transition: 'background 180ms ease',
              whiteSpace: 'nowrap',
            }}
            onMouseEnter={(e) => (e.currentTarget.style.background = ORANGE_HOVER)}
            onMouseLeave={(e) => (e.currentTarget.style.background = ORANGE)}
          >
            {dealCtaLabel(hasPrice)}
            <ArrowUpRight aria-hidden style={{ width: 14, height: 14, strokeWidth: 2.5 }} />
          </a>
        </div>
      </div>
    </article>
  )
}
